import { toast } from "sonner";
import { Activity } from "lucide-react";
import type { Agent } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { darfJetzt, useUserContext } from "@/auth/operations";
import { relativeTime } from "@/lib/relativeTime";
import { useAgentPulsMeta } from "@/api/queries/puls";
import { useUpdatePulsEnabled } from "@/api/mutations/puls";

// #335 (UC-5): der Puls am Agenten — Freischalt-Schalter + stille Meta-Zeilen.
//
// ⚠⚠ KEIN Inhalt und kein Link auf den Inhalt (Konzept Entscheidung 12): der Puls ist die
// Startseite eines MENSCHEN und enthält Mail-Inhalte und Entwürfe. Der Betreiber sieht,
// DASS und WANN etwas geschrieben wurde und wie gross es ist — mehr braucht er, um zu
// beurteilen, ob der Mechanismus arbeitet. Die Master-Route liefert ohnehin nichts anderes.

const QUELLE: Record<string, string> = {
  scheduled: "Zeitplan",
  manual: "manuell",
  chat: "aus dem Chat",
};

function groesse(bytes: number | null): string {
  if (bytes === null) return "—";
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

export function PulsSection({ agent }: { agent: Agent }) {
  const ctx = useUserContext();
  const darfSchalten = darfJetzt(ctx, "agent-puls.manage");
  const update = useUpdatePulsEnabled(agent.id);
  const meta = useAgentPulsMeta(agent.id);

  // Version-Skew-Hardening: ein älterer Master kennt das Feld nicht. Der Default ist AN —
  // wie bei einem frisch angelegten Agenten (Konzept Entscheidung 13).
  const aktiv = meta.data?.enabled ?? agent.pulsEnabled ?? true;

  function toggle(value: boolean) {
    update.mutate(value, {
      onSuccess: () => toast.success(`Puls: ${value ? "freigeschaltet" : "abgeschaltet"}`),
      onError: (err) => toast.error(`Puls schalten fehlgeschlagen: ${(err as Error).message}`),
    });
  }

  return (
    <SectionCard
      icon={<Activity className="h-[18px] w-[18px]" />}
      title="Puls"
      description="Die Startseite, die dieser Agent für seinen Nutzer baut"
      testId="puls-section"
    >
      <div className="space-y-3">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
            checked={aktiv}
            disabled={update.isPending || !darfSchalten}
            onChange={(e) => toggle(e.target.checked)}
            data-testid="agent-puls-enabled"
          />
          <span className="min-w-0">
            <span className="block text-[13px] font-semibold text-text">
              Puls für diesen Agenten
            </span>
            <span className="block text-[11.5px] text-text3">
              Aus: der Agent bekommt das Werkzeug nicht mehr, der Master weist seine Zugriffe ab
              und die App des Nutzers zeigt einen Hinweis. Die zuletzt gebaute Seite bleibt
              gespeichert und ist nach dem Wiedereinschalten sofort wieder da.
            </span>
          </span>
        </label>

        {!darfSchalten && (
          <p className="text-[11.5px] text-text3" data-testid="puls-kein-recht">
            Diesen Schalter darf nur ein Administrator bedienen.
          </p>
        )}

        <div className="border-t border-border pt-3" data-testid="puls-meta">
          {meta.isLoading && <p className="text-[11.5px] text-text3">Stand wird geladen …</p>}
          {meta.isError && (
            <p className="text-[11.5px] text-danger" data-testid="puls-meta-fehler">
              Stand nicht abrufbar.{" "}
              <button type="button" className="underline" onClick={() => void meta.refetch()}>
                Erneut laden
              </button>
            </p>
          )}
          {meta.data && meta.data.version === null && (
            <p className="text-[11.5px] text-text3" data-testid="puls-meta-leer">
              Noch keine Seite gebaut.
            </p>
          )}
          {meta.data && meta.data.version !== null && (
            <dl className="grid grid-cols-[110px_1fr] gap-y-1 text-[11.5px]">
              <dt className="text-text3">Version</dt>
              <dd className="text-text" data-testid="puls-meta-version">
                {meta.data.version}
              </dd>
              <dt className="text-text3">Zuletzt</dt>
              <dd className="text-text" data-testid="puls-meta-zeit">
                {relativeTime(meta.data.updatedAt)}
              </dd>
              <dt className="text-text3">Ausgelöst</dt>
              <dd className="text-text" data-testid="puls-meta-quelle">
                {meta.data.source ? (QUELLE[meta.data.source] ?? meta.data.source) : "—"}
              </dd>
              <dt className="text-text3">Größe</dt>
              <dd className="text-text" data-testid="puls-meta-groesse">
                {groesse(meta.data.bytes)}
              </dd>
            </dl>
          )}
          <p className="mt-2 text-[11.5px] text-text3">
            Der Inhalt der Seite ist hier bewusst nicht einsehbar — er gehört dem Nutzer.
          </p>
        </div>
      </div>
    </SectionCard>
  );
}
